'use client';
import {
  SVT_CORNER,
  SVT_FIELD,
  SVT_FIELD_INVALID,
} from '@eliadmin/core/src/components/ui/svetech-shape';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { forwardRef, useEffect, useRef, useState } from 'react';
import type { PhoneInputProps, CountryData } from 'react-international-phone';
import { PhoneInput, defaultCountries } from 'react-international-phone';
// The core styles of the library (the alias resolves `./phone-input-styles.css` only in the core).
import '../../../../node_modules/@eliadmin/core/src/components/ui/phone-input/phone-input-styles.css';

/*
 * SveTech field (Key design „Pole“): the wrapper is the white box of `Input` (1 px border, green on
 * focus without a ring, red when invalid, 56 → 50 px); the country button and the input inside are
 * transparent and fill its height. Both variants look the same.
 */
const FIELD = [
  'flex items-stretch overflow-hidden svt-body h-[var(--svt-field-h)] focus-within:border-[#2c7a25]',
  // The library sheet styles these parts with equal specificity, hence `!`.
  '[&_.react-international-phone-input-container]:!h-full [&_.react-international-phone-input-container]:w-full',
  '[&_.react-international-phone-country-selector-button]:!h-full [&_.react-international-phone-country-selector-button]:!bg-transparent',
  '[&_.react-international-phone-input]:!h-full [&_.react-international-phone-input]:!bg-transparent [&_.react-international-phone-input]:!text-[#14171a] [&_.react-international-phone-input]:![font-size:inherit]',
  '[&_.react-international-phone-input]:![font-family:inherit] [&_.react-international-phone-input]:![font-stretch:inherit]',
  '[&_.react-international-phone-country-selector::after]:!bg-[#e6e8e2]',
  SVT_FIELD,
  SVT_CORNER,
].join(' ');

type PhoneInputChangeMeta = Parameters<NonNullable<PhoneInputProps['onChange']>>[1];
type PhoneInputCountry = PhoneInputChangeMeta['country'] | CountryData;

const NORMALIZE_NATIONAL_TRUNK_PREFIX_COUNTRIES = new Set(['sk', 'cz']);
const DIAL_CODE_TO_COUNTRY: Record<string, string> = {
  '420': 'cz',
  '421': 'sk',
};

function getPhoneCountryIso2(country?: PhoneInputCountry) {
  if (!country) return undefined;

  if (Array.isArray(country)) {
    return country[1]?.toLowerCase();
  }

  return country.iso2?.toLowerCase();
}

function getPhoneCountryDialCode(country?: PhoneInputCountry) {
  if (!country) return undefined;

  if (Array.isArray(country)) {
    return country[2]?.replace(/\D/g, '');
  }

  return country.dialCode?.replace(/\D/g, '');
}

function normalizePhoneWithoutNationalTrunkPrefix(phone: string, country?: PhoneInputCountry) {
  if (!phone) return '';

  const digits = phone.replace(/\D/g, '');
  const dialCode =
    getPhoneCountryDialCode(country) ??
    Object.keys(DIAL_CODE_TO_COUNTRY).find(code => digits.startsWith(code));
  const iso2 =
    getPhoneCountryIso2(country) ?? (dialCode ? DIAL_CODE_TO_COUNTRY[dialCode] : undefined);

  if (!dialCode || !iso2 || !NORMALIZE_NATIONAL_TRUNK_PREFIX_COUNTRIES.has(iso2)) {
    return phone;
  }

  const numberAfterDialCode = digits.startsWith(dialCode) ? digits.slice(dialCode.length) : digits;

  if (!numberAfterDialCode.startsWith('0')) {
    return phone;
  }

  return `+${dialCode}${numberAfterDialCode.slice(1)}`;
}

export const PhoneInputField = forwardRef<
  HTMLInputElement,
  PhoneInputProps & { variant?: 'outlined' | 'default' }
>(function PhoneInputField({ className, variant, onChange, value, ...props }, ref) {
  const [focused, setFocused] = useState(false);
  const phoneInputRef = useRef(ref ?? null);
  const normalizedValue =
    typeof value === 'string' ? normalizePhoneWithoutNationalTrunkPrefix(value) : value;

  const customCountries = defaultCountries.map(country => {
    if (country[0] === 'Slovakia') {
      return ['Slovakia', 'sk', '421', '... ... ...'] as CountryData;
    }
    return country;
  });

  const locale = useLocaleStore(s => s.currentLocale);

  const defaultCountryFromLocale = () => {
    if (locale === 'cs') {
      return 'cz';
    }

    return locale;
  };

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      const current = phoneInputRef.current as any;

      if (current && !current.contains(event.target)) {
        setFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [phoneInputRef]);

  return (
    <div
      ref={phoneInputRef as any}
      className={cn(
        FIELD,
        focused && 'border-[#2c7a25]',
        // `aria-invalid` from `FormControl` is not a prop of the library (it does not reach the input).
        (props as { 'aria-invalid'?: boolean })['aria-invalid'] && SVT_FIELD_INVALID,
        variant === 'outlined' ? 'ea-phone-input--outlined' : 'ea-phone-input--default',
        className,
      )}
    >
      <PhoneInput
        defaultCountry={defaultCountryFromLocale()}
        value={normalizedValue}
        name="phone"
        countries={customCountries}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(phone, meta) => {
          onChange?.(normalizePhoneWithoutNationalTrunkPrefix(phone, meta.country), meta);
        }}
        inputClassName={cn('placeholder:text-[#6b7681]', props.inputClassName)}
        inputStyle={{
          paddingLeft: '20px',
          width: '100%',
          border: 'none',
        }}
        countrySelectorStyleProps={{
          buttonStyle: {
            paddingLeft: '12px',
            border: 'none',
          },
        }}
        {...props}
      />
    </div>
  );
});

PhoneInputField.displayName = 'PhoneInputField';
